import { MenuKey } from '@components/map/contextMenu/type';
import { getContextMenuCameraLabel } from '@utils/sensors/camera/camera.utils';
import { getSensorUniqueKey } from '@utils/sensors/sensors.utils';
import { LngLat } from 'mapbox-gl';

import { MenuItemOption } from '@/types/menu.types';
import { PopupType, PopupTypeEnum } from '@/types/popup.types';
import { CameraConfiguration } from '@/types/sensor/configuration.types';
import { CameraActiveMonitoringSensor } from '@/types/sensor/monitoring.types';

export type CommonContextMenuContext = HasEventContext & HasQrCodeContext & HasMeasureContext;

export type CommonMenuOptions = {
  createEvent: boolean;
  displayPositionQrCode: boolean;
  measure: boolean;
};

export type HasEventContext = {
  popups: PopupType[];
  accessEventCommand: boolean;
  isOpenedPopupByType: (popups: PopupType[], popup: PopupTypeEnum) => boolean;
  handleCreateEventClick: () => void;
};

export type HasQrCodeContext = {
  position: LngLat | null;
  openGpsQrCodePopup: () => void;
};

export type HasMeasureContext = {
  position: LngLat | null;
  label: string;
  enableMeasure: () => void;
};

export type HasPointCUASContext = {
  position: LngLat | null;
  accessCamCommand: boolean;
  movableLadCameras: CameraActiveMonitoringSensor[];
  handleLookAt: (cameraConfiguration: CameraConfiguration) => void;
};

export type HasEngageContext = {
  isEngagementOptionHidden: boolean;
  isEngagementOptionLoading: boolean;
  isEngagementOptionDisabled: boolean;
  handleEngagement: () => void;
};

export type HasDisengageContext = {
  isDisengagementOptionHidden: boolean;
  isDisengagementOptionLoading: boolean;
  isDisengagementOptionDisabled: boolean;
  handleDisengagement: () => void;
};

export const buildCreateEventOption = <C extends HasEventContext>(ctx: C): [MenuKey, MenuItemOption] => [
  'createEvent',
  {
    label: 'contextmenu.actions.createEvent',
    isHidden: !ctx.accessEventCommand,
    isDisabled: ctx.isOpenedPopupByType(ctx.popups, PopupTypeEnum.EVENT),
    onAction: ctx.handleCreateEventClick,
  },
];

export const buildQrCodeOption = <C extends HasQrCodeContext>(ctx: C): [MenuKey, MenuItemOption] => [
  'displayPositionQrCode',
  {
    label: 'contextmenu.actions.target.broadcastTarget.displayPositionQrCode',
    isDisabled: !ctx.position,
    onAction: ctx.openGpsQrCodePopup,
  },
];

export const buildMeasureOption = <C extends HasMeasureContext>(ctx: C): [MenuKey, MenuItemOption] => [
  'measure',
  {
    label: ctx.label,
    shouldNotTranslate: true,
    onAction: ctx.enableMeasure,
    isDisabled: !ctx.position?.lng || !ctx.position.lat,
  },
];

export const buildPointCUASOption = <C extends HasPointCUASContext>(ctx: C): [MenuKey, MenuItemOption] => [
  'pointCUAS',
  {
    label: 'contextmenu.actions.pointCamera',
    isHidden: !ctx.accessCamCommand,
    isDisabled: ctx.movableLadCameras.length === 0 || !ctx.position,
    subMenuItems: ctx.movableLadCameras.reduce(
      (acc, current) => ({
        ...acc,
        [getSensorUniqueKey(current)]: {
          label: getContextMenuCameraLabel(current),
          shouldNotTranslate: true,
          onAction: () => ctx.handleLookAt(current.configuration),
        },
      }),
      {},
    ),
  },
];

export const buildEngageOption = <C extends HasEngageContext>(ctx: C): [MenuKey, MenuItemOption] => [
  'engage',
  {
    label: 'contextmenu.actions.engage',
    isHidden: ctx.isEngagementOptionHidden,
    isLoading: ctx.isEngagementOptionLoading,
    isDisabled: ctx.isEngagementOptionDisabled,
    onAction: ctx.handleEngagement,
  },
];

export const buildDisengageOption = <C extends HasDisengageContext>(ctx: C): [MenuKey, MenuItemOption] => [
  'disengage',
  {
    label: 'contextmenu.actions.disengage',
    isHidden: ctx.isDisengagementOptionHidden,
    isLoading: ctx.isDisengagementOptionLoading,
    isDisabled: ctx.isDisengagementOptionDisabled,
    onAction: ctx.handleDisengagement,
  },
];
